/**
 * Ikasmak Entity - CNC Machine management
 * Manages machine state, door, loaded materials and tools
 */

/**
 * Parse item to extract base name and position
 */
function parseItem(item) {
    const match = item.match(/^(.+?)\(([^)]+)\)$/);
    if (match) {
        return {
            baseName: match[1].trim(),
            position: match[2],
            fullName: item
        };
    }
    return {
        baseName: item,
        position: null,
        fullName: item
    };
}

/**
 * Compare positions for sorting
 */
function comparePositions(posA, posB) {
    if (!posA && !posB) return 0;
    if (!posA) return 1;
    if (!posB) return -1;

    const parsePos = (pos) => {
        const match = pos.match(/(\d+),\s*(\d+),\s*(\d+)/);
        if (!match) return [0, 0, 0];
        return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
    };

    const [x1, y1, z1] = parsePos(posA);
    const [x2, y2, z2] = parsePos(posB);

    if (x1 !== x2) return x1 - x2;
    if (y1 !== y2) return y1 - y2;
    return z1 - z2;
}

/**
 * Sort items by name first, then by position
 */
function sortItems(items) {
    return items.sort((a, b) => {
        const itemA = parseItem(a);
        const itemB = parseItem(b);

        const nameComparison = itemA.baseName.localeCompare(itemB.baseName);
        if (nameComparison !== 0) {
            return nameComparison;
        }

        return comparePositions(itemA.position, itemB.position);
    });
}

class Ikasmak {
    constructor() {
        this.opened = false;
        this.contains = [];
        this.currentPiece = null; // Currently machined piece
        this.maxRawSlots = 1; // Maximum raw materials (brutos)
        this.maxToolSlots = 2; // Maximum tools (herramientas)
        // Pieces have unlimited slots

        // Optional: Known items from BT XML
        this.knownRaws = null;
        this.knownTools = null;
    }

    /**
     * Set known items from BT XML for dynamic validation
     * @param {Set} knownRaws - Set of raw material names
     * @param {Set} knownTools - Set of tool names
     */
    setKnownItems(knownRaws, knownTools) {
        this.knownRaws = knownRaws;
        this.knownTools = knownTools;
    }

    /**
     * Open the machine door
     * @returns {boolean} true if operation successful, false if already open
     */
    open() {
        if (this.opened) {
            return false; // Already open
        }
        this.opened = true;
        return true;
    }

    /**
     * Close the machine door
     * @returns {boolean} true if operation successful, false if already closed
     */
    close() {
        if (!this.opened) {
            return false; // Already closed
        }
        this.opened = false;
        return true;
    }

    /**
     * Check if machine door is open
     * @returns {boolean}
     */
    isOpen() {
        return this.opened;
    }

    /**
     * Determine item type
     * @param {string} item - Item to check
     * @param {Set} knownRaws - Optional set of known raw material names from BT
     * @param {Set} knownTools - Optional set of known tool names from BT
     * @returns {string} - "raw", "tool", or "piece"
     */
    getItemType(item, knownRaws = null, knownTools = null) {
        // Priority 1: Check against known tools from BT
        if (knownTools && knownTools.size > 0) {
            for (const toolName of knownTools) {
                if (item.includes(toolName)) {
                    return "tool";
                }
            }
        }

        // Priority 2: Check against known raws from BT
        if (knownRaws && knownRaws.size > 0) {
            for (const rawName of knownRaws) {
                if (item.includes(rawName)) {
                    return "raw";
                }
            }
        }

        // Priority 3: Check if it's a piece (contains "piece" or "pieza")
        const itemLower = item.toLowerCase();
        if (itemLower.includes('piece') || itemLower.includes('pieza')) {
            return "piece";
        }

        // Priority 4: Fallback heuristics when no known lists available
        // This maintains backward compatibility with old code that doesn't use known lists
        if (!knownTools && !knownRaws) {
            // Legacy pattern: Tools typically start with T + number (T1, T2, etc.)
            if (/^T\d+/i.test(item)) {
                return "tool";
            }
        }

        // Final fallback: if not identified as tool or piece, assume raw
        // This is safe because raw materials have the most flexible naming
        return "raw";
    }

    /**
     * Count items of specific type in contains array
     * @param {string} type - "raw", "tool", or "piece"
     * @returns {number}
     */
    countItemsByType(type) {
        return this.contains.filter(item => this.getItemType(item, this.knownRaws, this.knownTools) === type).length;
    }

    /**
     * Check if there is capacity for a specific item type
     * @param {string} item - Item to check
     * @returns {boolean}
     */
    hasCapacityFor(item) {
        const itemType = this.getItemType(item, this.knownRaws, this.knownTools);

        if (itemType === "raw") {
            return this.countItemsByType("raw") < this.maxRawSlots;
        } else if (itemType === "tool") {
            return this.countItemsByType("tool") < this.maxToolSlots;
        }
        // Pieces have unlimited capacity
        return true;
    }

    /**
     * Load material or tool into machine
     * @param {string} item - Item to load
     * @returns {boolean} - true if loaded successfully, false if no capacity
     */
    loadItem(item) {
        const itemType = this.getItemType(item, this.knownRaws, this.knownTools);

        // Check capacity before loading
        if (!this.hasCapacityFor(item)) {
            console.log(`[Ikasmak.loadItem] FAILED - No capacity for ${itemType}: "${item}"`);
            return false;
        }

        this.contains.push(item);
        const maxSlots = itemType === "raw" ? this.maxRawSlots : itemType === "tool" ? this.maxToolSlots : "unlimited";
        console.log(`[Ikasmak.loadItem] Loaded "${item}" (${this.countItemsByType(itemType)}/${maxSlots})`);
        return true;
    }

    /**
     * Unload item from machine
     * @param {string} item - Item to unload (base name, will match with or without position)
     * @returns {string|null} - Unloaded item or null if machine empty
     */
    unloadItem(item = null) {
        if (this.contains.length === 0) {
            return null;
        }

        if (item) {
            // Try exact match first
            let index = this.contains.indexOf(item);

            // If not found, try partial match (for items with positions)
            if (index === -1) {
                index = this.contains.findIndex(i => i.includes(item));
            }

            if (index !== -1) {
                return this.contains.splice(index, 1)[0];
            }
            console.log(`[Ikasmak.unloadItem] Item "${item}" not found`);
            return null;
        }

        return this.contains.pop();
    }

    /**
     * Get current state
     * @returns {Object}
     */
    getState() {
        // Sort items by name first, then by position (ascending)
        const sortedContains = sortItems ? sortItems([...this.contains]) : [...this.contains];

        return {
            opened: this.opened ? "abierto" : "cerrado",
            contains: sortedContains,
            currentPiece: this.currentPiece
        };
    }

    /**
     * Reset to initial state
     */
    reset() {
        this.opened = false;
        this.contains = [];
        this.currentPiece = null;
    }
}

// Universal export (Node.js + React)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = Ikasmak;
}
export default Ikasmak;
